import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

function isStandaloneApp() {
  return window.matchMedia?.("(display-mode: standalone)").matches || (navigator as any).standalone === true;
}

export function usePushNotifications() {
  const { user } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission>(() => ("Notification" in window ? Notification.permission : "denied"));
  const [isRegistering, setIsRegistering] = useState(false);

  const isSupported = useMemo(() => (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  ), []);

  const saveSubscription = useCallback(async (subscription: PushSubscription) => {
    if (!user) return;
    const json = subscription.toJSON();
    const endpoint = json.endpoint;
    const p256dh = json.keys?.p256dh;
    const auth = json.keys?.auth;
    if (!endpoint || !p256dh || !auth) throw new Error("Invalid push subscription");

    const payload = {
      user_id: user.id,
      endpoint,
      p256dh,
      auth,
      device_type: isStandaloneApp() ? "app" : "browser",
      user_agent: navigator.userAgent,
      last_seen_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data: existing } = await supabase
      .from("push_subscriptions")
      .select("id")
      .eq("endpoint", endpoint)
      .maybeSingle();

    if (existing?.id) {
      await supabase.from("push_subscriptions").update(payload).eq("id", existing.id);
    } else {
      await supabase.from("push_subscriptions").insert(payload);
    }
  }, [user]);

  const enable = useCallback(async (silent = false) => {
    if (!isSupported || !user) return false;
    setIsRegistering(true);
    try {
      const nextPermission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
      setPermission(nextPermission);
      if (nextPermission !== "granted") return false;

      const { data, error } = await supabase.functions.invoke("send-push-notification", { body: { action: "config" } });
      if (error) throw error;
      if (!data?.publicKey) throw new Error("Missing push public key");

      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      const subscription = existing || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });

      await saveSubscription(subscription);
      if (!silent) toast.success("התראות לנייד הופעלו");
      return true;
    } catch (error: any) {
      if (!silent) toast.error(error?.message || "לא ניתן להפעיל התראות במכשיר הזה");
      return false;
    } finally {
      setIsRegistering(false);
    }
  }, [isSupported, saveSubscription, user]);

  useEffect(() => {
    if (user && isSupported && Notification.permission === "granted") {
      void enable(true);
    }
  }, [enable, isSupported, user]);

  return { isSupported, permission, isRegistering, enable };
}
